import React, { useState, useEffect } from 'react';
import { Mail, MapPin, Phone, Github, Linkedin, Twitter, Edit3, Save, X } from 'lucide-react';
import { getContactInfo, saveContactInfo } from '../../services/homepageContentService';

const ContactInfoCard = ({ isAdmin, setMessage }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    email: "ryan@example.com",
    location: "San Francisco, CA",
    phone: "+1 (555) 123-4567",
    github: "github.com/yourusername",
    linkedin: "linkedin.com/in/yourusername",
    twitter: "@yourusername",
    website: "yourwebsite.com"
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadContactInfo = async () => {
      try {
        const data = await getContactInfo();
        if (data) {
          setContactInfo(data);
        }
      } catch (error) {
        console.error('Error loading contact info:', error);
      } finally {
        setLoading(false);
      }
    };
    loadContactInfo();
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      await saveContactInfo(contactInfo);
      setMessage("Contact information updated successfully!");
      setIsEditing(false);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving contact info:', error);
      setMessage("Failed to save contact information. Please try again.");
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="p-6 panel">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Helper function to check if a field has a value
  const hasValue = (value) => value && value.trim() !== '';

  if (!isAdmin) {
    // Show read-only version for non-admin users
    return (
      <div className="p-6 panel">
        <h3 className="font-serif text-lg font-semibold tracking-tight text-stone-900 border-b border-line pb-2 mb-4">Contact Information</h3>
        <div className="space-y-3">
          {hasValue(contactInfo.email) && (
            <div className="flex items-center space-x-3">
              <Mail className="w-4 h-4 text-stone-600" />
              <a href={`mailto:${contactInfo.email}`} className="text-sm text-gray-700 hover:text-indigo-600 transition">
                {contactInfo.email}
              </a>
            </div>
          )}
          
          {hasValue(contactInfo.location) && (
            <div className="flex items-center space-x-3">
              <MapPin className="w-4 h-4 text-stone-600" />
              <span className="text-sm text-gray-700">{contactInfo.location}</span>
            </div>
          )}
          
          {hasValue(contactInfo.phone) && (
            <div className="flex items-center space-x-3">
              <Phone className="w-4 h-4 text-stone-600" />
              <a href={`tel:${contactInfo.phone}`} className="text-sm text-gray-700 hover:text-indigo-600 transition">
                {contactInfo.phone}
              </a>
            </div>
          )}
          
          {hasValue(contactInfo.github) && (
            <div className="flex items-center space-x-3">
              <Github className="w-4 h-4 text-stone-600" />
              <a href={`https://${contactInfo.github}`} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-700 hover:text-indigo-600 transition">
                {contactInfo.github}
              </a>
            </div>
          )}
          
          {hasValue(contactInfo.linkedin) && (
            <div className="flex items-center space-x-3">
              <Linkedin className="w-4 h-4 text-stone-600" />
              <a href={`https://${contactInfo.linkedin}`} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-700 hover:text-indigo-600 transition">
                {contactInfo.linkedin}
              </a>
            </div>
          )}
          
          {hasValue(contactInfo.twitter) && (
            <div className="flex items-center space-x-3">
              <Twitter className="w-4 h-4 text-stone-600" />
              <a href={`https://twitter.com/${contactInfo.twitter.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-700 hover:text-indigo-600 transition">
                {contactInfo.twitter}
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 panel">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-serif text-lg font-semibold tracking-tight text-stone-900 border-b border-line pb-2">Contact Information</h3>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center space-x-1 px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition"
          >
            <Edit3 className="w-4 h-4" />
            <span>Edit</span>
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={contactInfo.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              value={contactInfo.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              className="field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={contactInfo.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className="field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">GitHub</label>
            <input
              type="text"
              value={contactInfo.github}
              onChange={(e) => handleInputChange('github', e.target.value)}
              placeholder="github.com/username"
              className="field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
            <input
              type="text"
              value={contactInfo.linkedin}
              onChange={(e) => handleInputChange('linkedin', e.target.value)}
              placeholder="linkedin.com/in/username"
              className="field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Twitter</label>
            <input
              type="text"
              value={contactInfo.twitter}
              onChange={(e) => handleInputChange('twitter', e.target.value)}
              placeholder="@username"
              className="field"
            />
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center space-x-1 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Saving...' : 'Save Changes'}</span>
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center space-x-1 px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition"
            >
              <X className="w-4 h-4" />
              <span>Cancel</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {hasValue(contactInfo.email) && (
            <div className="flex items-center space-x-3">
              <Mail className="w-4 h-4 text-stone-600" />
              <a href={`mailto:${contactInfo.email}`} className="text-sm text-gray-700 hover:text-indigo-600 transition">
                {contactInfo.email}
              </a>
            </div>
          )}
          
          {hasValue(contactInfo.location) && (
            <div className="flex items-center space-x-3">
              <MapPin className="w-4 h-4 text-stone-600" />
              <span className="text-sm text-gray-700">{contactInfo.location}</span>
            </div>
          )}
          
          {hasValue(contactInfo.phone) && (
            <div className="flex items-center space-x-3">
              <Phone className="w-4 h-4 text-stone-600" />
              <a href={`tel:${contactInfo.phone}`} className="text-sm text-gray-700 hover:text-indigo-600 transition">
                {contactInfo.phone}
              </a>
            </div>
          )}
          
          {hasValue(contactInfo.github) && (
            <div className="flex items-center space-x-3">
              <Github className="w-4 h-4 text-stone-600" />
              <a href={`https://${contactInfo.github}`} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-700 hover:text-indigo-600 transition">
                {contactInfo.github}
              </a>
            </div>
          )}
          
          {hasValue(contactInfo.linkedin) && (
            <div className="flex items-center space-x-3">
              <Linkedin className="w-4 h-4 text-stone-600" />
              <a href={`https://${contactInfo.linkedin}`} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-700 hover:text-indigo-600 transition">
                {contactInfo.linkedin}
              </a>
            </div>
          )}
          
          {hasValue(contactInfo.twitter) && (
            <div className="flex items-center space-x-3">
              <Twitter className="w-4 h-4 text-stone-600" />
              <a href={`https://twitter.com/${contactInfo.twitter.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-700 hover:text-indigo-600 transition">
                {contactInfo.twitter}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContactInfoCard;
